import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, message, Tooltip, Row, Col, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import {
  getMonthlyTests,
  deleteMonthlyTest,
} from '../../../services/testApi';
import MonthlyTestModal from './MonthlyTestModal';
import type { MonthlyTest, SelectedMaterial } from './types';

const AdminMonthlyTests: React.FC = () => {
  const [tests, setTests] = useState<MonthlyTest[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTest, setEditingTest] = useState<MonthlyTest | null>(null);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const data = await getMonthlyTests();
      setTests(data);
    } catch (e) {
      message.error('Ошибка при загрузке тестов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTests();
  }, []);

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: 'Вы уверены, что хотите удалить этот тест?',
      onOk: async () => {
        try {
          await deleteMonthlyTest(id);
          message.success('Тест удален');
          fetchTests();
        } catch (e) {
          message.error('Ошибка при удалении');
        }
      },
    });
  };

  const columns: TableColumnsType<MonthlyTest> = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    {
      title: 'Время (мин)',
      dataIndex: 'testTime',
      key: 'testTime',
      width: 120,
      render: (testTime: number) => Math.ceil(Number(testTime || 0) / 60),
    },
    {
      title: 'Материалов',
      dataIndex: 'materials',
      key: 'materialsCount',
      render: (materials: SelectedMaterial[] | undefined) => materials?.length || 0,
      width: 120,
    },
    {
      title: 'Всего вопросов',
      dataIndex: 'materials',
      key: 'totalQuestions',
      render: (materials: SelectedMaterial[] | undefined) =>
        materials?.reduce((sum, item) => sum + (item.questionsAmount || 0), 0) || 0,
      width: 140,
    },
    {
      title: 'Статус',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Активен' : 'Неактивен'}</Tag>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Настроить тест">
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setEditingTest(record);
                setIsModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h2>Ежемесячные сборные тесты</h2>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingTest(null);
              setIsModalVisible(true);
            }}
          >
            Новый тест
          </Button>
        </Col>
      </Row>

      <Table dataSource={tests} columns={columns} rowKey="id" loading={loading} />

      {isModalVisible && (
        <MonthlyTestModal
          test={editingTest}
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={fetchTests}
        />
      )}
    </div>
  );
};

export default AdminMonthlyTests;
